import { atom, useAtom, useAtomValue } from 'jotai';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/router';

import { FormLabelWrapper, Heading, Button, PhoneInput, TextInput } from '@restworld/ui-ds-base';
import { useAnalytics } from '@restworld/utils-common';

import { existingPhoneNumber as existingPhoneNumberAtom } from '../../../context/auth-flow';
import { missingFieldFormLoadingAtom } from './cv-errors-field-reinput';
import { InformationHeadLine } from './error-info-headline';
import { buildLoginUrlWithApplyIntent } from '../apply-intent';
import { loginClickedProperties } from '../existing-account-events';
import { useJobPositionId } from '../../../hooks';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IPhoneNumberConflictProps {
  formInputs: React.ReactNode[];
}
const setUpdateNumberFlagAtom = atom<boolean>(false);
const PhoneNumberConflict: React.FunctionComponent<IPhoneNumberConflictProps> = ({ formInputs }) => {
  const { t } = useTranslation();
  const [updateNumberFlag, setUpdateNumberFlag] = useAtom(setUpdateNumberFlagAtom);
  const existingPhoneNumber = useAtomValue(existingPhoneNumberAtom);
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const jobPositionId = useJobPositionId();

  return updateNumberFlag ? (
    <UpdatePhoneNumber formInputs={formInputs} />
  ) : (
    <div className="flex flex-col gap-4">
      <Heading>{`${t('onboarding-page:conflicting_phone_number_how_would_you_like_proceed')}`}</Heading>
      <FormLabelWrapper label="Phone Number" id="readOnly-email">
        <TextInput
          type="email"
          id="readOnly-email"
          placeholder={existingPhoneNumber.phoneNumber ?? '+39 XX XXXXXX'}
          rightIcon={LockClosedIcon}
          required
          readOnly
          hasLeftPadding={false}
        />
      </FormLabelWrapper>
      <Button
        onClick={() => {
          trackEvent(
            'existing_account_login_clicked',
            loginClickedProperties({ jobPositionId, placement: 'post_409', methodHint: 'phone' })
          );
          router.push(
            buildLoginUrlWithApplyIntent(window.location.href, { phoneNumber: existingPhoneNumber?.phoneNumber })
          );
        }}
        variant="primary"
        type="button"
        fullwidth
      >
        {t('onboarding-page:login_with_existing_phone_number')}
      </Button>
      <Button variant="secondary-neutral" fullwidth onClick={() => setUpdateNumberFlag(true)}>
        {t('onboarding-page:update_phone_number_and_create_a_new_profile')}
      </Button>
    </div>
  );
};
const UpdatePhoneNumber = ({ formInputs }: IPhoneNumberConflictProps) => {
  const loading = useAtomValue(missingFieldFormLoadingAtom);
  const [, setUpdateNumberFlag] = useAtom(setUpdateNumberFlagAtom);
  const { t } = useTranslation();
  const {
    formState: { errors: formErrors },
    control,
  } = useFormContext();
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <InformationHeadLine
          title={t('onboarding-page:update_the_phone_number')}
          showBackButton
          backButtonClickHandler={() => setUpdateNumberFlag(false)}
        />
      </div>
      {/* <Title fontSize="large" fontWeight="semibold" content={`${t('onboarding-page:update_the_phone_number')}`} /> */}
      {!formInputs && (
        <Controller
          control={control}
          name="phone_number"
          render={({ field }) => (
            <PhoneInput
              variant={formErrors?.phone_number ? 'error' : 'primary'}
              priorityCountries={['IT', 'FR', 'ES']}
              menuDialogStyles={['min-w-full']}
              {...field}
            />
          )}
        />
      )}
      {formInputs}
      <Button loading={loading} variant="primary" fullwidth type="submit">
        Update
      </Button>
    </div>
  );
};
export default PhoneNumberConflict;
